import React from 'react'
import ReactDOM from 'react-dom'
import { colors } from './fieldTheme'
import { ArrivalIllustration, DepartureIllustration, FreeIcon, TourIllustration, ChevronLeft, ChevronRight, ClearIcon, CheckIcon, AlertIcon } from './FieldIcons'

// Briques communes des écrans terrain en style « Liste native » (décision 23, charte apps terrain)

export const FLOW_TYPES = {
	arrival: { title: 'Inventaire d\'entrée', subtitle: 'Arrivée d\'un vacancier', color: colors.arrival, short: 'Entrée' },
	departure: { title: 'Inventaire de sortie', subtitle: 'Départ d\'un vacancier', color: colors.departure, short: 'Sortie' },
	free: { title: 'Inventaire hors séjour', subtitle: 'Rattaché à l\'emplacement, pas à un séjour', color: colors.free, short: 'Hors séjour' },
	tour: { title: 'Tour de terrain', subtitle: 'Un inventaire par emplacement', color: colors.tour, short: 'Tour' }
}

export function FlowIllustration({ type, size })
{
	if (type === 'arrival')
		return <ArrivalIllustration size={size} />
	if (type === 'departure')
		return <DepartureIllustration size={size} />
	if (type === 'free')
		return <FreeIcon size={Math.round(size * 0.7)} />
	return <TourIllustration size={size} />
}

export function Pastille({ type, size, illustrationSize })
{
	return (
		<span style={{ width: size, height: size, borderRadius: 999, background: FLOW_TYPES[type].color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
			<FlowIllustration type={type} size={illustrationSize} />
		</span>
	)
}

export function BackButton({ label, onClick })
{
	return (
		<div style={{ display: 'flex', alignItems: 'center', minHeight: 56, padding: '4px 20px 0' }}>
			<button
				type="button"
				className="field-solid"
				onClick={onClick}
				style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 18px 0 10px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
			>
				<ChevronLeft />
				{label}
			</button>
		</div>
	)
}

// Pastille à gauche, titre et sous-titre (canvas W5)
export function FlowHeading({ type })
{
	const flow = FLOW_TYPES[type]
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '24px 20px 0' }}>
			<Pastille type={type} size={68} illustrationSize={48} />
			<span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: 4 }}>
				<h1 style={{ fontSize: 26, fontWeight: 800, color: colors.text }}>{flow.title}</h1>
				<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{flow.subtitle}</span>
			</span>
		</div>
	)
}

export function SectionTitle({ children })
{
	return (
		<h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', padding: '32px 20px 12px' }}>{children}</h2>
	)
}

// Groupe de lignes sur fond blanc, séparées par un filet aligné sur le texte
export function ListGroup({ children, indent = 86 })
{
	const items = React.Children.toArray(children).filter(Boolean)
	return (
		<div style={{ margin: '0 20px', background: colors.surface, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
			{items.map((child, index) => (
				<React.Fragment key={child.key || index}>
					{index > 0 && <span aria-hidden="true" style={{ display: 'block', height: 1, background: colors.separator, marginLeft: indent }} />}
					{child}
				</React.Fragment>
			))}
		</div>
	)
}

export function ListRow({ icon, iconBackground, title, subtitle, titleSize = 21, minHeight = 72, trailing, disabled, selected, onClick })
{
	return (
		<button
			type="button"
			className="field-row"
			onClick={onClick}
			disabled={disabled}
			aria-pressed={selected === undefined ? undefined : selected}
			style={{
				backgroundImage: 'none',
				backgroundColor: selected ? '#F0EFFF' : (disabled ? '#E9ECF1' : colors.surface),
				minHeight,
				display: 'flex',
				alignItems: 'center',
				gap: 14,
				padding: '8px 20px',
				width: '100%',
				border: 0,
				textAlign: 'left',
				color: disabled ? '#5B6577' : colors.text,
				fontFamily: 'inherit',
				cursor: disabled ? 'not-allowed' : 'pointer'
			}}
		>
			{icon && (
				<span style={{ width: 52, height: 52, borderRadius: 999, background: iconBackground || 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
					{icon}
				</span>
			)}
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
				<span style={{ fontSize: titleSize, fontWeight: 800, color: selected ? colors.primary : 'inherit', overflowWrap: 'break-word', hyphens: 'auto', WebkitHyphens: 'auto' }}>{title}</span>
				{subtitle && <span style={{ fontSize: 15, fontWeight: 600, color: disabled ? '#5B6577' : colors.textSecondary, whiteSpace: 'pre-line' }}>{subtitle}</span>}
			</span>
			{trailing === undefined ? <ChevronRight /> : trailing}
		</button>
	)
}

// Champ de recherche arrondi, gros caractères, bouton d'effacement de 48 px (canvas W5, W5c)
export function SearchField({ id, icon, value, onChange, onClear, inputMode, placeholder, inputRef, clearLabel, onEnter })
{
	return (
		<div style={{ margin: '0 20px', minHeight: 64, borderRadius: 999, background: colors.surface, border: '3px solid ' + colors.primary, display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 0 18px' }}>
			{icon}
			<input
				id={id}
				ref={inputRef}
				type="text"
				inputMode={inputMode}
				pattern={inputMode === 'numeric' ? '[0-9]*' : undefined}
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize={inputMode === 'numeric' ? 'off' : 'characters'}
				spellCheck="false"
				enterkeyhint="search"
				value={value}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && onEnter)
						onEnter()
				}}
				className="browser-default"
				style={{ flex: 1, minWidth: 0, minHeight: 52, height: 52, margin: 0, padding: 0, border: 0, outline: 'none', boxShadow: 'none', background: 'transparent', fontSize: 22, fontWeight: 700, color: colors.text, fontFamily: 'inherit', letterSpacing: inputMode === 'numeric' ? '.04em' : 'normal' }}
			/>
			{value !== '' && (
				<button
					type="button"
					aria-label={clearLabel}
					onClick={onClear}
					style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', width: 48, height: 48, borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, flex: 'none', cursor: 'pointer' }}
				>
					<ClearIcon />
				</button>
			)}
		</div>
	)
}

export function HelpText({ children })
{
	return <p style={{ padding: '8px 20px 0', fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{children}</p>
}

// Message neutre (aucun résultat, erreur de réseau) : titre et conseil, annoncé aux lecteurs d'écran
export function Notice({ icon, title, hint, action })
{
	return (
		<div role="status" style={{ margin: '32px 20px 0', display: 'flex', alignItems: 'flex-start', gap: 14, padding: 20, borderRadius: 18, background: '#E8EBF5', color: colors.text }}>
			{icon}
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.35 }}>
				<span style={{ fontSize: 17, fontWeight: 700, color: colors.text, overflowWrap: 'break-word' }}>{title}</span>
				{hint && <span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{hint}</span>}
				{action}
			</span>
		</div>
	)
}

// Lien souligné de 52 px (« J'ai un numéro de séjour »)
export function LinkButton({ icon, children, onClick, withChevron })
{
	return (
		<button
			type="button"
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: 'transparent', display: 'inline-flex', alignItems: 'center', gap: 10, minHeight: 52, padding: '0 12px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left' }}
		>
			{icon}
			<span style={{ textDecoration: 'underline', textUnderlineOffset: 4, textDecorationThickness: 2 }}>{children}</span>
			{withChevron && <ChevronRight color="currentColor" strokeWidth={2.8} />}
		</button>
	)
}

// Bouton grisé : texte foncé sur gris clair, contraste d'environ 9:1 (le blanc sur gris faisait 1,8:1)
export const DISABLED = { background: '#D5DAE6', color: '#2A3147' }

export function PrimaryButton({ children, onClick, disabled, icon })
{
	return (
		<button
			type="button"
			className="field-solid"
			onClick={onClick}
			disabled={disabled}
			style={{ backgroundImage: 'none', backgroundColor: disabled ? DISABLED.background : colors.primary, flex: 1, minWidth: 0, minHeight: 64, borderRadius: 999, border: 0, color: disabled ? DISABLED.color : '#FFFFFF', fontSize: 19, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '6px 12px', textAlign: 'center', fontFamily: 'inherit', lineHeight: 1.2, cursor: disabled ? 'not-allowed' : 'pointer' }}
		>
			{icon}
			{children}
		</button>
	)
}

export function SecondaryButton({ children, onClick })
{
	return (
		<button
			type="button"
			className="field-solid"
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', flex: 1, minWidth: 0, minHeight: 64, borderRadius: 999, border: '2px solid #B9C0D4', color: colors.primary, fontSize: 17, fontWeight: 700, lineHeight: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 12px', textAlign: 'center', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			{children}
		</button>
	)
}

// Barre d'actions collée en bas de l'écran, sous le pouce (charte : actions principales en bas)
export function BottomBar({ children })
{
	return (
		<div style={{ position: 'sticky', bottom: 0, marginTop: 'auto', display: 'flex', gap: 16, padding: '16px 20px', paddingBottom: 'max(28px, env(safe-area-inset-bottom))', background: colors.background }}>
			{children}
		</div>
	)
}

export function LoadingText({ children })
{
	return <p role="status" style={{ padding: '32px 20px 0', fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>{children}</p>
}

// « M066 · Sortie » avec la petite pastille du type (en-têtes des écrans d'inventaire)
export function FlowChip({ type, location })
{
	return (
		<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 700, color: colors.text, minWidth: 0 }}>
			<Pastille type={type} size={32} illustrationSize={22} />
			<span style={{ overflowWrap: 'anywhere' }}>{location + ' · ' + FLOW_TYPES[type].short}</span>
		</span>
	)
}

// Ligne-lien à pastille (« Signaler un problème », « Commentaire et photos »)
export function RowLink({ icon, title, subtitle, onClick })
{
	return (
		<button
			type="button"
			className="field-row"
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: colors.surface, minHeight: 76, width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: 20, borderRadius: 18, border: 0, color: colors.text, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			<span aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 999, background: '#E8EBF5', color: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{icon}</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 17, fontWeight: 700 }}>{title}</span>
				{subtitle && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{subtitle}</span>}
			</span>
			<ChevronRight color={colors.primary} strokeWidth={2.8} />
		</button>
	)
}

// Champ de recherche au repos (canvas W7, W8) : pilule grise, libellé caché
export function SoftSearchField({ id, label, placeholder, value, onChange, onClear })
{
	return (
		<div style={{ minHeight: 56, display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 0 18px', borderRadius: 999, background: '#E8EBF5' }}>
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2.8" strokeLinecap="round" aria-hidden="true" style={{ flex: 'none' }}>
				<circle cx="11" cy="11" r="6.5" />
				<path d="M16 16l5 5" />
			</svg>
			<label htmlFor={id} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{label}</label>
			<input
				id={id}
				type="search"
				autoComplete="off"
				autoCorrect="off"
				spellCheck="false"
				enterkeyhint="search"
				className="browser-default"
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				style={{ flex: 1, minWidth: 0, height: 52, margin: 0, padding: 0, border: 0, outline: 'none', boxShadow: 'none', background: 'transparent', fontSize: 18, fontWeight: 600, color: colors.text, fontFamily: 'inherit', WebkitAppearance: 'none' }}
			/>
			{value !== '' && (
				<button
					type="button"
					aria-label="Effacer la recherche"
					onClick={onClear}
					style={{ backgroundImage: 'none', backgroundColor: '#FFFFFF', width: 44, height: 44, borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, flex: 'none', cursor: 'pointer' }}
				>
					<ClearIcon />
				</button>
			)}
		</div>
	)
}

// Barre d'action blanche collée en bas, avec ombre (canvas W8)
export function StickyActionBar({ children })
{
	return (
		<div style={{ position: 'sticky', bottom: 0, marginTop: 'auto', zIndex: 2, background: colors.surface, boxShadow: '0 -6px 10px -6px rgba(11,16,51,.22)', padding: '20px 20px', paddingBottom: 'max(24px, env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 8 }}>
			{children}
		</div>
	)
}

// Voile foncé derrière tous les volets (retour d'Alexandra du 24/10/2026) : on se concentre sur le volet
export const SHEET_VEIL = 'rgba(11,16,51,.78)'

// Clavier ouvert sur iPhone : il recouvre la page sans la réduire, et un volet collé en bas passait
// derrière (retour d'Alexandra du 24/10/2026). La zone visible (visualViewport) donne la place réelle.
// Sur iPhone, la barre « ⌃ ⌄ ✓ » qui flotte au-dessus du clavier n'est pas déduite de la zone visible :
// on lui laisse sa hauteur (mesurée sur la capture d'Alexandra du 24/10/2026).
const IOS_KEYBOARD_BAR = 52

function isIOS()
{
	return typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent || '')
}

function readViewport()
{
	if (typeof window === 'undefined' || !window.visualViewport)
		return { inset: 0, height: null }
	const viewport = window.visualViewport
	const covered = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop))
	if (covered === 0)
		return { inset: 0, height: Math.round(viewport.height) }
	const bar = isIOS() ? IOS_KEYBOARD_BAR : 0
	return { inset: covered + bar, height: Math.round(viewport.height) - bar }
}

export function useKeyboardInset()
{
	const [viewport, setViewport] = React.useState(readViewport)
	React.useEffect(() => {
		const visual = typeof window !== 'undefined' ? window.visualViewport : null
		if (!visual)
			return ;
		const update = () => setViewport(readViewport())
		visual.addEventListener('resize', update)
		visual.addEventListener('scroll', update)
		return () => {
			visual.removeEventListener('resize', update)
			visual.removeEventListener('scroll', update)
		}
	}, [])
	return viewport
}

// Hauteur maximale d'un volet. C'est la zone réellement visible (visualViewport) qui compte : avec les barres
// d'Safari ou le clavier, la hauteur de la page peut dépasser l'écran, et un volet calé dessus sortait par le
// haut (titre, croix et premier champ hors d'atteinte, sans défilement possible). Sans visualViewport, 100 % de la page.
// Clavier ouvert : toute la zone visible au-dessus du clavier ; sinon, 60 px laissés au voile en haut.
function sheetLimit(viewport)
{
	if (!viewport.height)
		return 'calc(100% - 60px)'
	if (viewport.inset > 0)
		return (viewport.height - 8) + 'px'
	return Math.max(240, viewport.height - 60) + 'px'
}

// Place d'un volet : au-dessus du clavier quand il est ouvert. full : hauteur fixe (volet de recherche,
// qui ne doit pas rétrécir quand il reste un seul résultat ou aucun).
function sheetPlacement(viewport, full)
{
	const limit = sheetLimit(viewport)
	return { bottom: viewport.inset, maxHeight: limit, height: full ? limit : 'auto' }
}

// Photo en plein écran (variante 720 px du serveur) : fond noir, photo entière, fermeture par la croix, un toucher
// ou Échap. Rendue à la racine de l'app, au-dessus des volets.
export function PhotoViewer({ url, alt, onClose })
{
	React.useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'Escape')
				onClose()
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [onClose])
	const target = typeof document !== 'undefined' ? (document.querySelector('.field-app') || document.body) : null
	const viewer = (
		<div role="dialog" aria-modal="true" aria-label={alt || 'Photo'} onClick={onClose} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 70, background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'manipulation' }}>
			<img src={url} alt={alt || ''} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} />
			<button
				type="button"
				aria-label="Fermer la photo"
				onClick={(e) => {
					e.stopPropagation()
					onClose()
				}}
				style={{ backgroundImage: 'none', backgroundColor: 'rgba(255,255,255,.18)', position: 'absolute', top: 'max(12px, env(safe-area-inset-top))', right: 12, width: 48, height: 48, borderRadius: 999, border: 0, padding: 0, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
			>
				<ClearIcon />
			</button>
		</div>
	)
	return target ? ReactDOM.createPortal(viewer, target) : viewer
}

// Volet du bas (canvas W7b, W9, W11) : voile, poignée, titre centré, groupes d'actions.
// Rendu à la racine de l'app (portail) : ouvert depuis un volet d'action (« Ajouter une photo »,
// « Attribuer à »), il passait sous son bouton du bas et ne défilait plus (retour d'Alexandra du 24/10/2026).
// footer : zone fixée en bas du volet (« Fermer »), la liste défile entre le titre et elle
export function BottomSheet({ title, text, full, footer, onClose, children })
{
	const viewport = useKeyboardInset()
	const keyboard = viewport.inset > 0
	const panelRef = React.useRef(null)
	React.useEffect(() => {
		const panel = panelRef.current
		if (!panel)
			return ;
		const firstButton = panel.querySelector('button')
		if (firstButton)
			firstButton.focus()
	}, [])
	const target = typeof document !== 'undefined' ? (document.querySelector('.field-app') || document.body) : null
	const sheet = (
		<React.Fragment>
			<div className="field-sheet-backdrop" onClick={onClose} style={{ position: 'fixed', inset: 0, background: SHEET_VEIL, zIndex: 50 }} />
			<div
				ref={panelRef}
				className="field-sheet"
				role="dialog"
				aria-modal="true"
				aria-label={title}
				onKeyDown={(e) => {
					if (e.key === 'Escape')
						onClose()
				}}
				style={{ position: 'fixed', left: 0, right: 0, zIndex: 51, maxWidth: 576, margin: '0 auto', background: colors.surface, borderRadius: '28px 28px 0 0', display: 'flex', flexDirection: 'column', ...sheetPlacement(viewport, full) }}
			>
				<div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 12, padding: keyboard ? '12px 20px' : '10px 20px 16px' }}>
					{!keyboard && <span aria-hidden="true" style={{ alignSelf: 'center', width: 44, height: 5, borderRadius: 3, background: '#B8BFCC' }} />}
					<h2 style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2, textAlign: 'center', color: colors.text }}>{title}</h2>
					{text && <p style={{ fontSize: 17, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>{text}</p>}
				</div>
				<div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: '0 20px', paddingBottom: footer || keyboard ? 16 : 'max(28px, env(safe-area-inset-bottom))' }}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						{children}
					</div>
				</div>
				{footer && (
					<div style={{ flex: 'none', padding: '12px 20px', paddingBottom: keyboard ? 12 : 'max(24px, env(safe-area-inset-bottom))', borderTop: '1px solid ' + colors.separator }}>
						{footer}
					</div>
				)}
			</div>
		</React.Fragment>
	)
	return target ? ReactDOM.createPortal(sheet, target) : sheet
}

export function SheetGroup({ children })
{
	const items = React.Children.toArray(children).filter(Boolean)
	return (
		<div style={{ flex: 'none', background: '#F2F3F7', borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
			{items.map((child, index) => (
				<React.Fragment key={child.key || index}>
					{index > 0 && <span aria-hidden="true" style={{ display: 'block', height: 1, background: colors.separator, margin: '0 20px' }} />}
					{child}
				</React.Fragment>
			))}
		</div>
	)
}

export function SheetRow({ label, icon, danger, onClick })
{
	return (
		<button
			type="button"
			className="field-row"
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: '#F2F3F7', minHeight: 60, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 20px', border: 0, color: danger ? '#7F1D1D' : colors.text, fontSize: 19, fontWeight: 700, textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}
		>
			<span>{label}</span>
			{icon}
		</button>
	)
}

// « Enregistrer l'inventaire » avec son état d'envoi et son message d'échec (récapitulatif, sans modèle)
export function SaveInventoryButton({ saving, saveError, onSave })
{
	let label = 'Enregistrer l\'inventaire'
	if (saving)
		label = 'Enregistrement…'
	else if (saveError)
		label = 'Réessayer l\'enregistrement'
	return (
		<React.Fragment>
			{saveError && !saving && <ErrorNote>L'inventaire n'a pas pu être enregistré. Vérifiez la connexion, puis réessayez : votre saisie est gardée.</ErrorNote>}
			<PrimaryButton icon={saving ? null : <CheckIcon />} disabled={saving} onClick={onSave}>{label}</PrimaryButton>
		</React.Fragment>
	)
}

// Message d'échec d'un envoi, annoncé aux lecteurs d'écran
export function ErrorNote({ children })
{
	return (
		<p role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderRadius: 14, background: '#FEE2E2', color: '#7F1D1D', fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>
			<AlertIcon size={22} color="#7F1D1D" />
			{children}
		</p>
	)
}

// En-tête replié pendant la frappe (recherche d'emplacement ou de séjour) : « ‹ Accueil » et la petite
// pastille du type, pour laisser les résultats au-dessus du clavier. Les tailles de texte ne changent pas.
export function CompactFlowHeader({ type, backLabel, onBack })
{
	return (
		<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 56, padding: '4px 20px 0' }}>
			<button
				type="button"
				className="field-solid"
				onClick={onBack}
				style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 18px 0 10px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', flex: 'none' }}
			>
				<ChevronLeft />
				{backLabel}
			</button>
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 700, color: colors.text }}>
				<Pastille type={type} size={32} illustrationSize={22} />
				{FLOW_TYPES[type].short}
			</span>
		</div>
	)
}

// Libellé lu par les lecteurs d'écran mais masqué à l'œil (champ en mode compact)
export const visuallyHidden = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }

// Remonte l'écran terrain tout en haut (le clavier iOS peut l'avoir décalé)
export function scrollFieldToTop(element)
{
	const container = element && element.closest ? element.closest('.field-app') : null
	if (container)
		container.scrollTop = 0
	if (typeof window !== 'undefined' && window.scrollTo)
		window.scrollTo(0, 0)
}

// Volet d'action (canvas W19b0 à W21) : voile foncé, poignée, bouton fermer rond à gauche, titre aligné
// à gauche sur une ligne et sous-titre, contenu qui défile, action principale collée en bas du volet.
// Clavier ouvert : poignée masquée, en-tête et pied resserrés, pour laisser la place au champ
// singleScroll (tour de terrain, sur téléphone) : titre, contenu et action principale défilent d'un seul bloc,
// rien n'est fixé en haut ni en bas du volet
export function ActionSheet({ title, subtitle, onClose, footer, singleScroll, children })
{
	const viewport = useKeyboardInset()
	const keyboard = viewport.inset > 0
	const handle = !keyboard && <span aria-hidden="true" style={{ alignSelf: 'center', width: 44, height: 5, borderRadius: 3, background: '#B8BFCC', marginTop: 10, flex: 'none' }} />
	const header = (
		<div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: keyboard ? '8px 20px' : '10px 20px 12px', flex: 'none' }}>
			<button
				type="button"
				aria-label="Fermer"
				onClick={onClose}
				style={{ backgroundImage: 'none', backgroundColor: colors.surface, width: 48, height: 48, borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, flex: 'none', boxShadow: '0 2px 10px rgba(11,16,51,.16)', cursor: 'pointer' }}
			>
				<ClearIcon />
			</button>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
				<h2 style={{ fontSize: 21, fontWeight: 800, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h2>
				{subtitle && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{subtitle}</span>}
			</span>
		</div>
	)
	const body = (
		<div style={singleScroll ? { flex: 'none', padding: '4px 20px 16px' } : { flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: '4px 20px 16px' }}>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				{children}
			</div>
		</div>
	)
	const bottom = footer && (
		<div style={{ flex: 'none', padding: keyboard ? '8px 20px' : '12px 20px', paddingBottom: keyboard ? 8 : 'max(24px, env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 8, borderTop: singleScroll ? 0 : '1px solid ' + colors.separator }}>
			{footer}
		</div>
	)
	// Rendu à la racine de l'app, comme BottomSheet : l'écran du tour est animé (transform), ce qui faisait de lui le
	// repère du `position: fixed` : le volet partait alors du bas de la page entière, hors de l'écran visible sur
	// téléphone, dans une zone qu'on ne pouvait pas faire défiler.
	const target = typeof document !== 'undefined' ? (document.querySelector('.field-app') || document.body) : null
	const sheet = (
		<React.Fragment>
			<div className="field-sheet-backdrop" onClick={onClose} style={{ position: 'fixed', inset: 0, background: SHEET_VEIL, zIndex: 40 }} />
			<div
				className="field-sheet"
				role="dialog"
				aria-modal="true"
				aria-label={title}
				onKeyDown={(e) => {
					if (e.key === 'Escape')
						onClose()
				}}
				style={{ position: 'fixed', left: 0, right: 0, zIndex: 41, maxWidth: 576, margin: '0 auto', background: colors.surface, borderRadius: '28px 28px 0 0', display: 'flex', flexDirection: 'column', ...sheetPlacement(viewport, false) }}
			>
				{singleScroll ? (
					<div style={{ flex: '0 1 auto', minHeight: 0, maxHeight: sheetLimit(viewport), overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column' }}>
						{handle}
						{header}
						{body}
						{bottom}
					</div>
				) : (
					<React.Fragment>
						{handle}
						{header}
						{body}
						{bottom}
					</React.Fragment>
				)}
			</div>
		</React.Fragment>
	)
	return target ? ReactDOM.createPortal(sheet, target) : sheet
}
